import { useEffect, useRef, useState } from 'react'
import {
  Map as MlMap, NavigationControl, Popup, ScaleControl, setWorkerUrl,
  type GeoJSONSource,
} from 'maplibre-gl'
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'maplibre-gl/dist/maplibre-gl.css'
import { buildRasterStyle, rasterBasemap } from '../map/basemaps'
import { iconFor, MARKER_COLOR, registerVehicleIcons, type MarkerState } from '../map/layers/markers'
import { addReferenceLayers, SRC_FENCES, updateReference } from '../map/layers/reference'
import { isLaden, loadLabel } from '../map/layers/vehicles'
import { isUsableFixEpoch, pruneTweens, tweenedPosition, type TweenStore } from '../map/layers/fixTween'
import { Panel } from '../cc/Panel'
import { ago, lastSeen, vehicleState } from '../lib/format'
import type { GeoPayload, RouteProgress, Vehicle, VehicleMeta } from '../lib/types'

let workerSet = false
function ensureWorker(): void {
  if (workerSet) return
  setWorkerUrl(workerUrl)
  workerSet = true
}

// Pipalkhunt's own centroid (db/geo/wondercement-pipalkhunt/geofences.json,
// PIPALKHUNT-1) -- rough fallback centre before the first real fix arrives, same pattern
// EimPortsMap.tsx's SITE_FALLBACK_CENTER uses.
const FALLBACK_CENTER: [number, number] = [74.5353302, 23.7220724]

function toMarkerState(v: Vehicle, now: number): MarkerState {
  return vehicleState(v, now)
}

function toGeoJSON(
  vehicles: Vehicle[], now: number, meta: Map<string, VehicleMeta>, progress: Map<string, RouteProgress>,
  /** Pre-tweened display position per vehicle (fixTween.ts), computed by the caller's
   *  own stateful tick loop -- this function stays pure. Falls back to the vehicle's
   *  raw (lat, lon) when a vehicle has no entry. */
  displayPositions?: ReadonlyMap<string, { lat: number; lon: number }>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: vehicles
      .filter((v): v is Vehicle & { lat: number; lon: number } => v.lat != null && v.lon != null)
      .map((v) => {
        const state = toMarkerState(v, now)
        // isLaden() -- the SAME helper CommandCentre.tsx's own fleet cards use for
        // Ultratech -- not a second laden/empty rule for this fleet. Its `progress`
        // argument is what actually answers this for Wonder Cement: quixapp's live
        // route-direction matcher already carries a `wondercement` entry in its own
        // FLEET_ROUTE_IDS (wondercement-laden/wondercement-empty, matching the published
        // corridor geometry), so a vehicle's snapped route_id resolves laden/empty
        // continuously from live GPS -- no geofence sets_load_state needed, which is as
        // well: Wonder Cement's gates are single bidirectional points (confirmed against
        // db/geo/wondercement-*.attributes.csv's own header), not one-way carriageway
        // pairs like Ultratech's, so per-fence sets_load_state cannot express direction
        // here even in principle. No `meta`/`progress` entry yet (nothing has arrived
        // over route.progress for this vehicle) falls through to false -- the same
        // "always empty" fallback this used to hardcode, until real data arrives.
        const laden = isLaden(meta.get(v.vehicle_id), undefined, progress.get(v.vehicle_id))
        // fixTween.ts's real-fix-to-real-fix lerp -- the SAME shared engine MapCanvas.tsx
        // uses for Ultratech -- glides from the last displayed position to each new real
        // fix, over the measured gap between fixes. A feed arriving every ~32s otherwise
        // sits a marker still for half that window, then jumps, which reads as "not live"
        // (exactly what was reported). Computed fresh on every RAF tick by the caller (see
        // the animation loop below), falling back to the raw fix for a vehicle with no
        // entry yet.
        const pos = displayPositions?.get(v.vehicle_id) ?? { lat: v.lat, lon: v.lon }
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
          properties: {
            id: v.vehicle_id,
            state,
            laden,
            icon: iconFor(state, laden),
            // Route-matched bearing over raw GPS course -- see map/layers/vehicles.ts's
            // own comment on buildVehicleFeatures' identical choice. heading_deg alone is
            // what made the truck icon face away from the road it's actually on.
            heading: progress.get(v.vehicle_id)?.route_bearing_deg ?? v.heading_deg ?? 0,
            soc: v.soc_pct,
            speed: v.speed_kph,
          },
        }
      }),
  }
}

/** Tweened display position per vehicle, fed into toGeoJSON -- shared by the mount-time
 *  addSource call and the RAF tick below so both compute it the same way. */
function tweenDisplayPositions(
  store: TweenStore, vehicles: Vehicle[], now: number,
): Map<string, { lat: number; lon: number }> {
  return new Map(
    vehicles
      .filter((v): v is Vehicle & { lat: number; lon: number } => v.lat != null && v.lon != null)
      .flatMap((v) => {
        // A vehicle whose origin epoch isn't usable (e.g. both last_geo_ts and
        // updated_ts falsy) is left out entirely -- toGeoJSON falls back to its raw fix,
        // same as the deleted coastPosition's own `if (!origin) return raw fix` guard,
        // rather than storing a bad anchor that would freeze the display on every later
        // real fix (adversarial review, Phase 4).
        const origin = v.last_geo_ts || v.updated_ts
        if (!isUsableFixEpoch(origin, now)) return []
        return [[v.vehicle_id, tweenedPosition(store, v.vehicle_id, { lat: v.lat, lon: v.lon }, origin, now)] as const]
      }),
  )
}

function median(ns: number[]): number {
  const s = [...ns].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2
}

const CLUSTER_RADIUS_KM = 3

function fitToVehicles(map: MlMap, vehicles: Vehicle[]): void {
  const pts = vehicles.filter((v): v is Vehicle & { lat: number; lon: number } => v.lat != null && v.lon != null)
  if (pts.length === 0) return
  if (pts.length === 1) {
    map.easeTo({ center: [pts[0]!.lon, pts[0]!.lat], zoom: 12, duration: 500 })
    return
  }
  const medLat = median(pts.map((v) => v.lat))
  const medLon = median(pts.map((v) => v.lon))
  const core = pts.filter((v) => {
    const dLatKm = (v.lat - medLat) * 111
    const dLonKm = (v.lon - medLon) * 105
    return Math.hypot(dLatKm, dLonKm) <= CLUSTER_RADIUS_KM
  })
  const fitset = core.length > 0 ? core : pts
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity
  for (const v of fitset) {
    minLon = Math.min(minLon, v.lon); maxLon = Math.max(maxLon, v.lon)
    minLat = Math.min(minLat, v.lat); maxLat = Math.max(maxLat, v.lat)
  }
  map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 56, duration: 500, maxZoom: 15 })
}

function popupHtml(p: Record<string, unknown>): string {
  const state = String(p.state ?? '')
  const soc = p.soc != null ? `${Math.round(Number(p.soc))}% SOC` : null
  const speed = p.speed != null ? `${p.speed} km/h` : null
  return (
    `<div style="min-width:150px;font-family:Inter,sans-serif">`
    + `<div style="font:700 12px ui-monospace,monospace;color:#0F172A">${p.id}</div>`
    + `<div style="font:600 13px Inter,sans-serif;color:#0F172A;margin-top:4px;text-transform:capitalize">${state}</div>`
    + `<div style="font:11px Inter,sans-serif;color:#64748B;margin-top:2px">${[soc, speed].filter(Boolean).join(' · ') || '—'}</div>`
    + `</div>`
  )
}

/** The click popup -- a persistent information card (closeButton, stays open until
 *  dismissed), same role BusRouteMap.tsx's own onBusClick popup plays for MBMT. The hover
 *  popup above is a glance-only preview; this is the full card the FLEET tab's own
 *  VehicleCard already shows, so a click on the map answers the same question a click in
 *  the panel does. `load` is the caller's own loadLabel() result (real meta/progress
 *  lookup, same as the FLEET tab) -- not re-derived here, so null (genuinely unknown)
 *  renders as no badge instead of a guessed one. */
function clickPopupHtml(v: Vehicle, state: MarkerState, load: 'Loaded' | 'Unloaded' | null, now: number): string {
  const stateLabel = state.charAt(0).toUpperCase() + state.slice(1)
  const soc = v.soc_pct != null ? `${Math.round(v.soc_pct)}%` : '—'
  const speed = v.speed_kph != null ? `${Math.round(v.speed_kph)} km/h` : '—'
  const seen = `${ago(lastSeen(v), now)} ago`
  return (
    `<div style="min-width:170px;font-family:Inter,sans-serif">`
    + `<div style="display:flex;align-items:center;gap:6px">`
    + `<span style="font:700 13px ui-monospace,monospace;color:#0F172A">${v.vehicle_id}</span>`
    + (load ? `<span style="font:600 10px Inter,sans-serif;color:#92400E;background:#FEF3C7;border-radius:4px;padding:1px 6px;text-transform:uppercase">${load}</span>` : '')
    + `</div>`
    + `<div style="font:600 13px Inter,sans-serif;color:#0F172A;margin-top:4px">${stateLabel}</div>`
    + `<div style="margin-top:6px;display:grid;grid-template-columns:auto auto;gap:1px 10px;font:11px Inter,sans-serif;color:#64748B">`
    + `<span>Speed</span><span style="color:#0F172A">${speed}</span>`
    + `<span>SOC</span><span style="color:#0F172A">${soc}</span>`
    + `<span>Updated</span><span style="color:#0F172A">${seen}</span>`
    + `</div>`
    + `</div>`
  )
}

/** Wonder Cement's own live map -- same maplibre/raster-tile/hand-drawn-icon pattern
 *  EimPortsMap.tsx uses, not MapCanvas.tsx (that component's live fallback reads
 *  useFleet() directly and is Ultratech-only by convention -- see its own comment at the
 *  s.vehicles.values() call site). vehicles comes in as a prop, already filtered to
 *  fleet === 'transvolt-wondercement' by the caller (WonderCementShell's LiveView), so this
 *  component has no fleet-filtering logic of its own to get wrong.
 *
 *  `geo` is the caller's already-filtered `wondercementGeo()` output (Wonder Cement's
 *  own route/fence slice of the global `/api/geo` payload) -- this component draws
 *  whatever it is handed via the SAME `addReferenceLayers`/`updateReference` pair
 *  MapCanvas.tsx uses for Ultratech's corridor, rather than a second copy of that layer
 *  logic. `focusVehicleId`/`focusFenceId` fly the camera to one vehicle's own fix, or to
 *  a geofence's centroid (looked up from the SAME `geo` prop the fence layers above are
 *  drawn from -- one source of truth for "where is this fence"), via a LOCAL effect on
 *  this component's own map instance -- not `useFleet`'s global `flyTo`/`flyRequest`,
 *  whose only reader today is Ultratech's MapCanvas. */
const EMPTY_META: Map<string, VehicleMeta> = new Map()
const EMPTY_PROGRESS: Map<string, RouteProgress> = new Map()

export function WonderCementMap(
  { vehicles, geo, meta, progress, focusVehicleId, focusFenceId }: {
    vehicles: Vehicle[]
    geo: GeoPayload
    /** TIMS overlay, keyed by vehicle_id -- same shared store field (`useFleet`'s `meta`)
     *  CommandCentre.tsx reads. Optional so a caller/test with no load-state data at all
     *  doesn't have to construct an empty Map just to satisfy the prop. */
    meta?: Map<string, VehicleMeta>
    /** Live route-direction match, keyed by vehicle_id -- same shared store field
     *  (`useFleet`'s `progress`), populated by quixapp's generic route.progress matcher.
     *  This is what actually drives Wonder Cement's laden/empty icon (see toGeoJSON's own
     *  comment) -- `meta.load_state` is Ultratech's own sets_load_state fallback and
     *  structurally cannot apply to Wonder Cement's bidirectional gates. */
    progress?: Map<string, RouteProgress>
    focusVehicleId?: string | null
    focusFenceId?: string | null
  },
): JSX.Element {
  const container = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MlMap | null>(null)
  const vehiclesRef = useRef<Vehicle[]>(vehicles)
  const metaRef = useRef<Map<string, VehicleMeta>>(meta ?? EMPTY_META)
  const progressRef = useRef<Map<string, RouteProgress>>(progress ?? EMPTY_PROGRESS)
  // Real-fix-to-real-fix lerp state (fixTween.ts), mount-scoped like the RAF loop that
  // reads it -- replaces the old coastPosition dead-reckoning guess.
  const tweenStoreRef = useRef<TweenStore>(new Map())
  const geoRef = useRef<GeoPayload>(geo)
  const fittedRef = useRef(false)
  const highlightedFenceRef = useRef<string | null>(null)
  const clickPopupRef = useRef<Popup | null>(null)
  // Flips once after map.on('load') adds the reference/truck sources -- a dependency the
  // focus effects below can re-run on, so a focusVehicleId/focusFenceId already set at
  // MOUNT time (not just set later by a click, well after load has long fired in
  // practice) still applies once the map is actually ready, instead of silently no-oping
  // forever because its one guarded attempt landed too early.
  const [styleLoaded, setStyleLoaded] = useState(false)
  vehiclesRef.current = vehicles
  metaRef.current = meta ?? EMPTY_META
  progressRef.current = progress ?? EMPTY_PROGRESS
  geoRef.current = geo

  // In an effect, not the render body (adversarial review, Phase 4): a StrictMode
  // double-render or a discarded concurrent render would otherwise wipe the tween store
  // on a render that never commits, snapping every truck on the next real one.
  useEffect(() => {
    pruneTweens(tweenStoreRef.current, new Set(
      vehicles.filter((v) => v.lat != null && v.lon != null).map((v) => v.vehicle_id),
    ))
  }, [vehicles])

  useEffect(() => {
    if (!container.current) return
    ensureWorker()
    const basemap = rasterBasemap('osm')
    if (!basemap) return
    const map = new MlMap({
      container: container.current,
      style: buildRasterStyle(basemap),
      center: FALLBACK_CENTER,
      zoom: 9,
      attributionControl: { compact: true },
    })
    mapRef.current = map
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left')

    const hoverPopup = new Popup({ closeButton: false, closeOnClick: false, offset: 18 })

    map.on('load', () => {
      // The shipped reference-layer builder -- same one MapCanvas.tsx calls for
      // Ultratech's own corridor -- draws the laden/empty route line (casing + solid +
      // dashed) and the fence fill/outline/badge/label layers from `geoRef.current`
      // (Wonder Cement's own slice, already filtered by the caller's wondercementGeo()).
      // Added BEFORE 'wc-trucks' on purpose: MapLibre paints layers in the order they were
      // added (later = on top), so the route line and fence fills were sitting over the
      // truck icons -- a truck crossing the corridor line visually vanished under it.
      addReferenceLayers(map, geoRef.current)

      registerVehicleIcons(map)
      map.addSource('wc-trucks', {
        type: 'geojson', data: toGeoJSON(
          vehiclesRef.current, Date.now(), metaRef.current, progressRef.current,
          tweenDisplayPositions(tweenStoreRef.current, vehiclesRef.current, Date.now()),
        ),
      })
      map.addLayer({
        id: 'wc-trucks',
        type: 'symbol',
        source: 'wc-trucks',
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          // Same curve Ultratech's own MapCanvas.tsx uses for this exact icon set
          // (map/layers/vehicles.ts) -- the smaller curve this had before rendered Wonder
          // Cement's trucks at roughly 60% of Ultratech's size at every comparable zoom,
          // for no reason (same base image, same markers.ts).
          'icon-size': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 12, 0.78, 14, 0.95, 16, 1.2],
        },
      })

      fitToVehicles(map, vehiclesRef.current)
      fittedRef.current = true

      map.on('mouseenter', 'wc-trucks', (e) => {
        map.getCanvas().style.cursor = 'pointer'
        const f = e.features?.[0]
        if (!f || f.geometry.type !== 'Point') return
        hoverPopup.setLngLat(f.geometry.coordinates as [number, number])
          .setHTML(popupHtml(f.properties ?? {})).addTo(map)
      })
      map.on('mouseleave', 'wc-trucks', () => {
        map.getCanvas().style.cursor = ''
        hoverPopup.remove()
      })

      // Click -> a persistent information card (same role BusRouteMap.tsx's own
      // onBusClick plays for MBMT) -- the hover popup above is a glance-only preview that
      // vanishes the moment the cursor moves, not an actual click target. Looks the real
      // row up from vehiclesRef.current (the feature's own properties only ever carry
      // what toGeoJSON copies onto it for rendering) so the card matches the FLEET tab's
      // own VehicleCard exactly, load badge included.
      map.on('click', 'wc-trucks', (e) => {
        const f = e.features?.[0]
        if (!f) return
        const vehicleId = String(f.properties?.id ?? '')
        const v = vehiclesRef.current.find((x) => x.vehicle_id === vehicleId)
        if (!v || v.lat == null || v.lon == null) return
        const state = toMarkerState(v, Date.now())
        const load = loadLabel(metaRef.current.get(v.vehicle_id), undefined, progressRef.current.get(v.vehicle_id))
        clickPopupRef.current?.remove()
        clickPopupRef.current = new Popup({ closeButton: true, maxWidth: '220px' })
          .setLngLat([v.lon, v.lat])
          .setHTML(clickPopupHtml(v, state, load, Date.now()))
          .addTo(map)
      })

      setStyleLoaded(true)
    })

    return () => {
      clickPopupRef.current?.remove()
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Continuous redraw, not just when a new poll lands -- the fixTween lerp (toGeoJSON's
  // own comment) needs a fresh `now` every tick to glide a moving truck between this
  // feed's ~32s packets instead of sitting still and then jumping, which is what "no live
  // movement" actually was. Same RAF-loop shape MapCanvas.tsx uses for Ultratech's own
  // trucks, but UNTHROTTLED: Wonder Cement's fleet (6 trucks today) costs nothing to
  // redraw at full 60fps. Reads refs, not vehicles/meta/progress props directly, so this
  // effect is mount-only and never needs to restart.
  useEffect(() => {
    let raf = 0
    const tick = (): void => {
      raf = requestAnimationFrame(tick)
      const map = mapRef.current
      const src = map?.getSource('wc-trucks') as GeoJSONSource | undefined
      if (!map || !src) return
      const list = vehiclesRef.current
      const now = Date.now()
      src.setData(toGeoJSON(
        list, now, metaRef.current, progressRef.current,
        tweenDisplayPositions(tweenStoreRef.current, list, now),
      ))
      if (!fittedRef.current && list.some((v) => v.lat != null && v.lon != null)) {
        fitToVehicles(map, list)
        fittedRef.current = true
      }
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  // /api/geo resolves after this component's own first paint (the empty-geo edge case in
  // SPEC.md), so the route/fence layers above are added once on load with whatever
  // `geoRef.current` holds at that moment, then kept live here -- same
  // "setData on the existing source, never a second addSource" pattern the vehicles
  // effect above already uses for `wc-trucks`.
  //
  // NOT guarded on map.isStyleLoaded(): that call is routinely false right after
  // addReferenceLayers adds the two GeoJSON sources above (maplibre's own loaded() also
  // waits on in-flight tiles/images), which would make this effect silently -- and
  // permanently, since `geo` only changes once per /api/geo fetch -- drop the one update
  // that actually carries the route/fence data. reference.ts's own header already
  // documents exactly this failure mode in production ("the corridor line was reported
  // missing entirely ... no console error"). updateReference's own `instanceof
  // GeoJSONSource` check is what makes a too-early call here a safe no-op instead.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    updateReference(map, geo)
  }, [geo])

  // Click-to-focus from the Fleet tab's VehicleCard list (WonderCementShell's own
  // selection state, not useFleet's global flyTo/flyRequest -- see this component's own
  // header comment). A vehicle with no fix yet (lat/lon null, e.g. RJ09GF2231/2233 live
  // today) or a plate that has dropped off the roster is a no-op, not a fly to (0,0)/NaN.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !focusVehicleId) return
    const v = vehiclesRef.current.find((x) => x.vehicle_id === focusVehicleId)
    if (!v || v.lat == null || v.lon == null) return
    map.flyTo({ center: [v.lon, v.lat], zoom: Math.max(map.getZoom(), 13), duration: 600 })
  }, [focusVehicleId, styleLoaded])

  // Click-to-focus from the GEOFENCES tab's GeofenceCard list -- AC-7: flies to the
  // fence's own centroid AND lights it up on the map (SRC_FENCES is added with
  // `promoteId: 'geofence_id'` in reference.ts, so the feature-state id below is the same
  // geofence_id the GeofenceCard list keys on). Same no-op-on-unknown-id guard as the
  // vehicle focus above -- a fence id that no longer matches a feature in `geo` must not
  // fly anywhere or set state on a feature that doesn't exist.
  //
  // Guarded on the SRC_FENCES source actually existing: maplibre's own setFeatureState
  // calls _checkLoaded() and THROWS ("Style is not done loading") rather than failing
  // silently when the style/source isn't ready yet -- a real crash path, not the
  // isStyleLoaded()-false no-op the geo-update effect above relies on. Skipping the whole
  // operation (not just the throwing call) when the source is missing also avoids the
  // ref lying about what's actually highlighted on the map.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.getSource(SRC_FENCES)) return
    if (highlightedFenceRef.current) {
      map.setFeatureState({ source: SRC_FENCES, id: highlightedFenceRef.current }, { selected: false })
      highlightedFenceRef.current = null
    }
    if (!focusFenceId) return
    const fence = geoRef.current.geofences.features.find(
      (f) => f.properties.geofence_id === focusFenceId,
    )
    if (!fence) return
    map.setFeatureState({ source: SRC_FENCES, id: focusFenceId }, { selected: true })
    highlightedFenceRef.current = focusFenceId
    const { centroid_lat, centroid_lon } = fence.properties
    if (centroid_lat == null || centroid_lon == null) return
    map.flyTo({ center: [centroid_lon, centroid_lat], zoom: Math.max(map.getZoom(), 14), duration: 600 })
  }, [focusFenceId, styleLoaded])

  return (
    <Panel
      title={`Pipalkhunt ↔ Pratapgarh ↔ Nimbahera · live positions${vehicles.length ? ` · ${vehicles.length}` : ''}`}
      pad={false}
      className="h-[560px]"
    >
      <div className="relative h-full w-full">
        <div ref={container} className="h-full w-full" />
        <div className="pointer-events-none absolute bottom-3 left-3 z-10 flex gap-2 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-medium text-[#475569] shadow-sm backdrop-blur">
          {(Object.keys(MARKER_COLOR) as MarkerState[])
            .filter((s) => s !== 'down')
            .map((s) => (
              <span key={s} className="flex items-center gap-1 capitalize">
                <span className="h-2 w-2 rounded-full" style={{ background: MARKER_COLOR[s] }} />
                {s}
              </span>
            ))}
        </div>
      </div>
    </Panel>
  )
}
